import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

/** Input con anteprima dei risultati: evita errori di battitura su comuni e vie. */
export const SuggestInput = ({
  value,
  onChange,
  onPick,
  fetcher,
  renderItem,
  placeholder,
  disabled,
  testId,
  minChars = 2,
  debounceMs = 450,
  emptyHint = 'Nessun risultato: controlla come hai scritto',
}) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const boxRef = useRef(null);
  const skipRef = useRef(false);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return undefined; }
    const q = (value || '').trim();
    if (!touched || disabled || q.length < minChars) { setItems([]); return undefined; }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetcher(q);
        if (!active) return;
        setItems(res || []);
        setOpen(true);
      } catch { if (active) setItems([]); } finally { if (active) setLoading(false); }
    }, debounceMs);
    return () => { active = false; clearTimeout(t); setLoading(false); clearTimeout(t); };
  }, [value, touched, disabled, minChars, debounceMs, fetcher]);

  const pick = (item) => {
    skipRef.current = true;
    setOpen(false);
    setItems([]);
    onPick(item);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={(e) => { setTouched(true); onChange(e.target.value); }}
        onFocus={() => items.length && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={testId}
      />
      {loading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-foreground/40">…</span>
      )}
      {open && (
        <div
          className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-border bg-white shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {items.length === 0 && !loading && (
            <div className="px-3 py-2 text-xs text-foreground/60">{emptyHint}</div>
          )}
          {items.map((item, i) => (
            <button
              key={`${testId}-opt-${i}`}
              type="button"
              onClick={() => pick(item)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 transition-colors"
              data-testid={`${testId}-option-${i}`}
            >
              {renderItem(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestInput;
